import { Box, Stack, Typography } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { memo, useMemo } from 'react'
import SubtleShimmer from '../common/SubtleShimmer'

const ROW_COUNT = 3

type CalendarWeekCardsSkeletonProps = {
    start: Dayjs
    end: Dayjs
    selectedDate?: Dayjs | null
}

function CalendarWeekCardsSkeleton({ start, end, selectedDate }: CalendarWeekCardsSkeletonProps) {
    const dates = useMemo(() => {
        const result: Dayjs[] = []
        for (
            let date = start;
            date.isBefore(end) || date.isSame(end, 'day');
            date = date.add(1, 'day')
        ) {
            result.push(date)
        }
        return result
    }, [start, end])

    return (
        <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            {dates.map((date) => {
                const isSelected = selectedDate?.isSame(date, 'day') ?? false
                const isToday = date.isSame(dayjs(), 'day')
                return (
                    <Box
                        key={date.format('YYYY-MM-DD')}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            minHeight: 132,
                            borderRadius: 2,
                            border: isSelected ? '0px' : '1px solid',
                            borderColor: isSelected ? 'none' : 'divider',
                            bgcolor: isSelected ? 'action.hover' : 'background.paper',
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Typography
                            sx={{
                                mb: 1,
                                fontWeight: 500,
                                textDecoration: isToday ? 'underline' : 'none',
                                textUnderlineOffset: '6px',
                                textDecorationThickness: '3px'
                            }}
                        >
                            {date.format('ddd DD')}
                        </Typography>
                        <SubtleShimmer loading>
                            <Stack spacing={0.75} sx={{ width: '100%' }}>
                                {Array.from({ length: ROW_COUNT }).map((__, j) => (
                                    <Box
                                        key={j}
                                        sx={{
                                            width: '100%',
                                            height: 14,
                                            borderRadius: 0.5
                                        }}
                                    />
                                ))}
                            </Stack>
                        </SubtleShimmer>
                    </Box>
                )
            })}
        </Stack>
    )
}

export default memo(CalendarWeekCardsSkeleton)
